import MessageReactions from './MessageReactions';
import React, { useMemo } from 'react';
import { StyleSheet, View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import MessageActions from '@/components/Messages/MessageActions';
import MessageContent from '@/components/Messages/MessageContent';
import MessageButtons from '@/components/Messages/MessageButtons';
import MessageImages from '@/components/Messages/MessageImages';
import MessageFiles from '@/components/Messages/MessageFiles';
import MessageQuote from '@/components/Messages/MessageQuote';
import Button from '@/components/Buttons/Button';
import Text from '@/components/Typography/Text';
import useResponsiveness from '@/hooks/useResponsiveness';
import Icon from '@/components/Icon';
import MessageThinkingContent from '@/components/Messages/MessageThinkingContent';
import MessageExpandable from '@/components/Messages/MessageExpandable';
import CreditsDepletedMessage from '@/components/Messages/CreditsDepletedMessage';
import PlusRequiredMessage from '@/components/Messages/PlusRequiredMessage';
import ContentModerationMessage from '@/components/Messages/ContentModerationMessage';
import { useAuthStore, useUIStore, useChatStore } from '@/store';
import PropTypes from 'prop-types';
import moment from 'moment-mini';
import { MESSAGE_LAYOUT, MESSAGE_WIDTH, MESSAGE_WIDTH_AMOUNTS, RESPONSE_ACTION_TYPES } from '@/constants/config';
import { isDarkTheme } from '@/constants/Colors';
import { getTemporaryUserBubbleStyle } from '@/constants/utils/temporaryChatStyles';
import MessageInferenceJob from '@/components/Messages/MessageInferenceJob';
import { getMessageDisplayInfo, computeImageAwareIsWaiting, shouldShowMessageTimestamp } from '@/constants/utils/message';
import { openUploadedChatFile, openMessageFile } from '@/constants/utils/files';
import { imagesNotCoveredByFiles } from '@/constants/utils/message_file_groups';
import {
  messageHasEditorInsertContent,
  messageHasMarkdownTable,
} from '@/constants/utils/message_editor_insert';
import { isCreditMessageError as isCreditError, isPlusMessageError as isPlusError } from '@/constants/utils/response_errors';

const formatMessageTime = (timestamp) => {
  const messageTime = moment(timestamp || Date.now());
  const now = moment();
  const diffDays = now.diff(messageTime, 'days');
  const time = messageTime.format('LT');
  if (diffDays === 0) return time;
  if (diffDays === 1) return `${time} · Yesterday`;
  if (diffDays <= 3) return `${time} · ${messageTime.format('ddd')}`;
  return `${time} · ${messageTime.format('D MMM')}`;
};

// Helper to check if error is a content moderation rejection
const isModerationError = (message) => {
  return message?.buttons?.some(btn => btn?.action === RESPONSE_ACTION_TYPES.CONTENT_MODERATION);
};

const HOVER_DELAY = 300;

// Static styles that don't depend on theme
const staticStyles = StyleSheet.create({
  errorContentWrapper: {
    marginTop: 5,
  },
  errorIcon: {
    marginRight: 8,
    marginTop: 0,
    flexShrink: 0,
  },
  errorContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  messageContentWrapper: {
    flex: 1,
  },
  preferenceButtonContainer: {
    marginTop: 8,
    marginBottom: 8,
    alignItems: 'flex-start',
  },
  regularMessageContainer: {},
});

const ErrorContent = React.memo(({ conversationId, message, colors, isUser }) => {
  const errorBubbleStyle = useMemo(() => ({
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 18,
    backgroundColor: 'rgba(254, 0, 0, 0.15)',
    borderWidth: 1,
    borderColor: 'rgba(254, 0, 0, 0.25)',
    marginBottom: 8,
    maxWidth: '100%',
  }), []);

  return (
    <View style={errorBubbleStyle}>
      <View style={staticStyles.errorContent}>
        <View style={staticStyles.errorContentWrapper}>
          <Icon 
            name="circle-exclamation" 
            size={16} 
            color={colors.color_destructive} 
            style={staticStyles.errorIcon} 
          />
        </View>
        <View style={staticStyles.messageContentWrapper}>
          <MessageContent 
            conversationId={conversationId}
            messageLayout={MESSAGE_LAYOUT.BUBBLE}
            isUser={isUser}
            messageId={message.id}
            citations={message.citations}
            products={message.products}
            stockQuotes={message.stockQuotes}
            places={message.places}
            map={message.map}
            content={message.content} 
            isError={message.isError}
            hasError={message.hasError}
            isDeleted={message?.flags?.isDeleted}
          />
        </View>
      </View>
    </View>
  );
});

const RegularContent = React.memo(({ conversationId, message, isUser, colors }) => {
  const { theme } = useTheme();
  const isTemporaryChat = useChatStore((state) => (
    state.chats.find(c => c.id === conversationId)?.flags?.isTemporary === true
  ));
  const temporaryBubbleStyle = useMemo(
    () => (isUser && isTemporaryChat ? getTemporaryUserBubbleStyle(theme) : null),
    [isUser, isTemporaryChat, theme],
  );
  const bubbleStyle = useMemo(() => ({
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: isUser ? colors.solid_color_user_bubble : colors.solid_color_tertiary,
    marginBottom: 0,
    marginLeft: isUser ? 'auto' : 0,
    // Disable transitions during streaming to prevent scroll jumping on older browsers
    ...(Platform.OS === 'web' && {
      transition: 'opacity 0.3s ease-out',
    }),
    ...(Platform.OS === 'web' ? { overflow: temporaryBubbleStyle ? 'visible' : 'hidden', maxWidth: '100%', flexShrink: 1 } : { flexShrink: 0 }),
    ...(message.images?.length > 0 && !message.content && {
      padding: 0,
      paddingHorizontal: 0,
      paddingVertical: 0,
    }),
    ...(temporaryBubbleStyle || {}),
  }), [colors.solid_color_user_bubble, colors.solid_color_tertiary, isUser, message.images, message.content, temporaryBubbleStyle]);

  const regularMessageContainerStyles = staticStyles.regularMessageContainer;

  const { shouldDisplayMessage, hasAnyInferencesWithIncompleteJobs, badStatuses } = getMessageDisplayInfo(message, isUser, { ignoreFiles: true });

  if (!shouldDisplayMessage) return null;

  return (
    <View style={regularMessageContainerStyles}>
      <View style={bubbleStyle}>
        {!!message.thinkingContent && !message.isThinking && !message.isStreaming && !message.isWaiting && !message.isRequestingMedia && (
          <MessageThinkingContent
            conversationId={conversationId}
            messageId={message.id}
            thinkingContent={message.thinkingContent}
            thinkingDurationMs={message.thinkingDurationMs}
            thinkingStartTime={message.thinkingStartTime}
            isThinking={message.isThinking}
            inferenceStatus={message.inferenceStatus}
            inferenceStage={message.inferenceStage}
            messageLayout={MESSAGE_LAYOUT.BUBBLE}
          />
        )}
        {isUser ? (
          <MessageExpandable
            content={message.content}
            fadeColor={colors.solid_color_user_bubble}
            align="left"
          >
            <MessageContent
              conversationId={conversationId}
              messageId={message.id}
              content={message.content}
              citations={message.citations}
              products={message.products}
              stockQuotes={message.stockQuotes}
              places={message.places}
              map={message.map}
              messageLayout={MESSAGE_LAYOUT.BUBBLE}
              isUser={isUser}
              isStreaming={message.isStreaming}
              isWaiting={computeImageAwareIsWaiting(message)}
              isRequestingMedia={message.isRequestingMedia}
              isTyping={message.isTyping}
              modelIds={message.modelIds}
              agentIds={message.agentIds}
              isDeleted={message.flags?.isDeleted}
              inferenceStatus={message.inferenceStatus}
              inferenceStage={message.inferenceStage}
              inferenceElapsedMs={message.inferenceElapsedMs}
            />
          </MessageExpandable>
        ) : (
          <MessageContent
            conversationId={conversationId}
            messageId={message.id}
            content={message.content}
            citations={message.citations}
            products={message.products}
            stockQuotes={message.stockQuotes}
            places={message.places}
            map={message.map}
            messageLayout={MESSAGE_LAYOUT.BUBBLE}
            isUser={isUser}
            isStreaming={message.isStreaming}
            isWaiting={computeImageAwareIsWaiting(message)}
            isRequestingMedia={message.isRequestingMedia}
            isTyping={message.isTyping}
            modelIds={message.modelIds}
            agentIds={message.agentIds}
            isDeleted={message.flags?.isDeleted}
            inferenceStatus={message.inferenceStatus}
            inferenceStage={message.inferenceStage}
            inferenceElapsedMs={message.inferenceElapsedMs}
          />
        )}
        {hasAnyInferencesWithIncompleteJobs && (
          <View style={{ marginTop: 8 }}>
            {message.inferences.map((inf) => (inf?.output?.jobs || [])
              .filter((job) => badStatuses.has(job?.status))
              .map((job) => (
                <MessageInferenceJob
                  key={`${inf.id || 'inf'}-${job.id || Math.random()}`}
                  conversationId={conversationId}
                  messageId={message.id}
                  inferenceId={inf.id}
                  job={job}
                />
              ))
            )}
          </View>
        )}
        {message.images && message.images.length > 0 && (
          <MessageImages 
            conversationId={conversationId}
            images={imagesNotCoveredByFiles(message.images, message.files)}
            onImagePress={(image) => {
              console.log('Image pressed:', image);
            }}
          />
        )}
        {isUser && message.isEdited && (
          <Text size="xs" color="secondary" style={{ opacity: 0.6, marginTop: 2, textAlign: 'right' }}>
            (edited)
          </Text>
        )}
      </View>
    </View>
  );
});

const MessageBubbleContent = React.memo(({
  conversationId,
  message,
  colors,
  isUser,
  onOpenLightbox,
  onOpenFileEditor,
}) => {
  // Check if this is a credit-related error (show friendly message instead of error UI)
  if ((message.isError || message.hasError) && isCreditError(message)) {
    return <CreditsDepletedMessage message={message} />;
  }

  // Check if this is a Plus-required error (show friendly upgrade message)
  if ((message.isError || message.hasError) && isPlusError(message)) {
    return <PlusRequiredMessage message={message} />;
  }

  // Check if this is a content moderation error
  if ((message.isError || message.hasError) && isModerationError(message)) {
    return <ContentModerationMessage message={message} />;
  }
  
  if (message.isError || message.hasError || message?.flags?.isDeleted) {
    return <ErrorContent conversationId={conversationId} message={message} colors={colors} isUser={isUser} />;
  }
  const fileCards = Array.isArray(message.files) && message.files.length > 0 ? (
    <MessageFiles
      files={message.files}
      isSmall={isUser}
      align={isUser ? 'right' : 'left'}
      isEditable={!isUser}
      onEdit={(file) => {
        if (file && file?.cdnPath && !isUser) {
          openMessageFile(file, {
            conversationId,
            onOpenLightbox,
            openToEditor: true,
            withInpainting: true,
            withChatInput: true,
          });
        } else if (file && file?.id && isUser) {
          // Uploaded docx/xlsx/csv/pptx open in their editor on desktop
          // web; everything else keeps the file details modal.
          if (!openUploadedChatFile(file, {
            conversationId,
            onOpenLightbox,
            openToEditor: false,
            withInpainting: true,
            withChatInput: true,
          })) onOpenFileEditor(file.id);
        } else {
          console.log('File EDIT pressed but do nothing:', file);
        }
      }}
      onPress={(file) => {
        if (file && file?.cdnPath && !isUser) {
          openMessageFile(file, {
            conversationId,
            onOpenLightbox,
            openToEditor: false,
            withInpainting: true,
            withChatInput: true,
          });
        } else if (file && file?.id && isUser) {
          // Uploaded docx/xlsx/csv/pptx open in their editor on desktop
          // web; everything else keeps the file details modal.
          if (!openUploadedChatFile(file, {
            conversationId,
            onOpenLightbox,
            openToEditor: false,
            withInpainting: true,
            withChatInput: true,
          })) onOpenFileEditor(file.id);
        } else {
          console.log('File pressed but do nothing:', file);
        }
      }}
    />
  ) : null;

  return (
    <>
      {isUser && !!message.quoteContent && (
        <MessageQuote quoteContent={message.quoteContent} />
      )}

      {/* User uploads stay above the bubble; assistant artifacts go below so
          "Open it below" lands next to the card when chat scrolls to the end. */}
      {isUser ? fileCards : null}

      <RegularContent
        conversationId={conversationId}
        message={message}
        colors={colors}
        isUser={isUser}
      />

      {!isUser ? fileCards : null}
    </>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.conversationId === nextProps.conversationId &&
    prevProps.message.isError === nextProps.message.isError &&
    prevProps.message.hasError === nextProps.message.hasError &&
    prevProps.message.content === nextProps.message.content &&
    prevProps.message.inferenceStatus === nextProps.message.inferenceStatus &&
    prevProps.message.inferenceStage === nextProps.message.inferenceStage &&
    prevProps.message.inferenceElapsedMs === nextProps.message.inferenceElapsedMs &&
    prevProps.message.thinkingContent === nextProps.message.thinkingContent &&
    prevProps.message.thinkingDurationMs === nextProps.message.thinkingDurationMs &&
    prevProps.message.thinkingStartTime === nextProps.message.thinkingStartTime &&
    prevProps.message.isThinking === nextProps.message.isThinking &&
    prevProps.message.isStreaming === nextProps.message.isStreaming &&
    prevProps.message.isWaiting === nextProps.message.isWaiting &&
    prevProps.message.isRequestingMedia === nextProps.message.isRequestingMedia &&
    prevProps.message.isTyping === nextProps.message.isTyping &&
    prevProps.message.isEdited === nextProps.message.isEdited &&
    prevProps.message.flags?.isSaved === nextProps.message.flags?.isSaved &&
    prevProps.message.flags?.isDeleted === nextProps.message.flags?.isDeleted &&
    prevProps.message.flags?.isGroupedResponsePreferred === nextProps.message.flags?.isGroupedResponsePreferred &&
    // Arrays are replaced wholesale by store updates (never mutated in
    // place) — reference equality is sufficient (see Message.jsx).
    prevProps.message.images === nextProps.message.images &&
    prevProps.message.citations === nextProps.message.citations &&
    prevProps.message.products === nextProps.message.products &&
    prevProps.message.stockQuotes === nextProps.message.stockQuotes &&
    prevProps.message.places === nextProps.message.places &&
    prevProps.message.map === nextProps.message.map &&
    prevProps.message.buttons === nextProps.message.buttons &&
    prevProps.message.inferences === nextProps.message.inferences &&
    prevProps.message.modelIds === nextProps.message.modelIds &&
    prevProps.message.agentIds === nextProps.message.agentIds &&
    prevProps.message.files === nextProps.message.files &&
    prevProps.colors === nextProps.colors &&
    prevProps.isUser === nextProps.isUser
  );
});

const MobileMessage = React.memo(({ 
  conversationId,
  message, 
  messageWidth,
  colors,
  theme,
  isUser, 
  isLastMessage,
  showActions, 
  showCopy,
  showAddToEditor,
  showAddToDocument,
  showAddToSpreadsheet,
  showTimestamps,
  onCopy,
  onAddToEditor,
  onAddToDocument,
  onAddToSpreadsheet,
  onShare,
  onSpeak,
  onRegenerate,
  onFork,
  onStartNewTask,
  onBookmark,
  onFeedback,
  onEdit,
  onReport,
  onDelete,
  onExport,
  onOpenLightbox,
  onOpenFileEditor,
  onButtonClick,
  userName,
  userAvatar,
  showPreferenceButton,
  showPreferenceIcons,
  onPreferResponse,
  onRejectResponse,
  isInMessageGroup,
  isLastGroupInChat,
  isAutoMode = false,
  displayModelIds,
  botAvatar,
}) => {  
  const containerStyle = useMemo(() => ({
    marginBottom: 8,
    flexDirection: 'row',
    justifyContent: isUser ? 'flex-end' : 'flex-start',
    width: '100%',
    maxWidth: MESSAGE_WIDTH_AMOUNTS[messageWidth?.toUpperCase() || 'NARROW'] || MESSAGE_WIDTH_AMOUNTS.NARROW,
    alignSelf: 'center',
    paddingHorizontal: 16,
  }), [isUser, messageWidth]);

  const wrapperStyle = useMemo(() => ({
    maxWidth: '92.5%',
    position: 'relative',
    minWidth: 0,
    ...(Platform.OS === 'web' ? { flexShrink: 1, height: 'auto' } : { flexShrink: botAvatar ? 1 : 0 }),
  }), [botAvatar]);

  return (
    <View style={containerStyle}>
      {message.role === 'assistant' ? botAvatar : null}
      <View style={wrapperStyle}>
        <MessageBubbleContent conversationId={conversationId} message={message} colors={colors} isUser={isUser} onOpenLightbox={onOpenLightbox} onOpenFileEditor={onOpenFileEditor} />
        {isUser && <MessageReactions conversationId={conversationId} message={message} />}
        {shouldShowMessageTimestamp(message, { isUser, showTimestamps }) && (
          <Text size="xs" style={{ opacity: 0.5, marginTop: 3, textAlign: isUser ? 'right' : 'left', paddingHorizontal: 4 }}>
            {formatMessageTime(message.timestamp)}
          </Text>
        )}
        {showActions && (
          <View style={{ marginTop: 4 }}>
            <MessageActions
              conversationId={conversationId}
              message={message}
              isUser={isUser}
              userName={userName}
              userAvatar={userAvatar}
              isVisible={true}
              isCompact={true}
              onCopy={showCopy ? onCopy : undefined}
              onAddToEditor={showAddToEditor ? onAddToEditor : undefined}
              onAddToDocument={showAddToDocument ? onAddToDocument : undefined}
              onAddToSpreadsheet={showAddToSpreadsheet ? onAddToSpreadsheet : undefined}
              onShare={onShare}
              onSpeak={showCopy ? onSpeak : undefined}
              onRegenerate={isInMessageGroup ? null : onRegenerate}
              onFork={onFork}
              onStartNewTask={onStartNewTask}
              onBookmark={onBookmark}
              onFeedback={onFeedback}
              onEdit={onEdit}
              onReport={onReport}
              onExport={onExport}
              onDelete={onDelete}
              showAttribution={true}
              workActions={message.workActions}
              isLastMessage={isLastMessage}
              modelIds={displayModelIds || message.modelIds}
              agentIds={message.agentIds}
              isAutoMode={isAutoMode}
              inferences={message.inferences}
              isImage={Array.isArray(message.images) && message.images.length > 0}
              isFile={Array.isArray(message.files) && message.files.length > 0}
              isBookmarked={message?.flags?.isSaved === true}
              parameters={message.parameters}
              createdAt={message.timestamp}
              showPreferenceIcons={showPreferenceIcons}
              isGroupedResponsePreferred={message?.flags?.isGroupedResponsePreferred}
              onPreferResponse={onPreferResponse}
              onRejectResponse={onRejectResponse}
            />
          </View>
        )}
        {showPreferenceButton && onPreferResponse && (
          <View style={staticStyles.preferenceButtonContainer}>
            <Button
              text="I prefer this response"
              variant={isDarkTheme(theme) ? 'secondary' : 'tertiary'}
              size="small"
              onPress={onPreferResponse}
            />
          </View>
        )}
        {Array.isArray(message.buttons) && message.buttons.length > 0 && 
         !((message.isError || message.hasError) && (isCreditError(message) || isPlusError(message) || isModerationError(message))) && (
          <View>
            <MessageButtons 
              conversationId={conversationId}
              messageId={message.id}
              buttons={message.buttons} 
              onButtonClick={onButtonClick}
            />
          </View>
        )}
      </View>
    </View>
  );
});

const DesktopMessage = React.memo(({ 
  conversationId,
  message, 
  colors,
  theme,
  isUser, 
  isStreaming: _isStreaming,
  showActions, 
  showCopy,
  showAddToEditor,
  showAddToDocument,
  showAddToSpreadsheet,
  showTimestamps,
  isXS,
  isLastMessage,
  onCopy,
  onAddToEditor,
  onAddToDocument,
  onAddToSpreadsheet,
  onShare,
  onSpeak,
  onRegenerate,
  onFork,
  onStartNewTask,
  onBookmark,
  onFeedback,
  onEdit,
  onExport,
  onReport,
  onDelete,
  onButtonClick,
  messageWidth,
  showMessageActions,
  onOpenLightbox,
  onOpenFileEditor,
  userName,
  userAvatar,
  isInMessageGroup,
  showPreferenceButton,
  showPreferenceIcons,
  onPreferResponse,
  onRejectResponse,
  isAutoMode = false,
  displayModelIds,
  botAvatar,
}) => {
  const containerStyle = useMemo(() => ({
    marginBottom: 8,
    flexDirection: 'row',
    position: 'relative',
    justifyContent: isUser ? 'flex-end' : 'flex-start',
    width: '100%',
    maxWidth: 
      messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW : 
      messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM : '100%',
    alignSelf: 'center',
  }), [isUser, messageWidth]);

  const wrapperStyle = useMemo(() => ({
    maxWidth: isInMessageGroup ? '100%' : (isXS ? '90%' : '80%'),
    // Assistant bubbles need a definite max width + overflow clamp so product
    // carousels / wide media can't inflate the chat column.
    ...(!isUser ? { width: '100%', minWidth: 0, overflow: 'hidden' } : { minWidth: 200 }),
    position: 'relative',
    height: 'auto',
    // Never transition height/transform/padding — those fight scroll position
    // on mobile browsers when streaming ends and actions/layout settle.
    ...(Platform.OS === 'web' && {
      transition: 'opacity 0.3s ease-out',
      // Isolate layout recalculations to prevent scroll jumping on older browsers
      contain: 'layout',
      boxSizing: 'border-box',
    }),
    paddingBottom: (isLastMessage) ? 36 : 0,
    flexShrink: 1,
  }), [isLastMessage, isInMessageGroup, isUser, isXS]);

  const actionsStyle = useMemo(() => ({
    position: showMessageActions ? 'relative' : 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: ((isLastMessage) || showMessageActions) ? 34 : 0,
    overflow: 'visible',
    opacity: ((isLastMessage) || showMessageActions) ? 1 : 0,
    // Opacity-only: height/transform transitions yank the scroll viewport
    // when the action bar appears after streaming completes.
    ...(Platform.OS === 'web' ? {
      transition: 'opacity 0.3s ease-out',
      transform: ((isLastMessage) || showMessageActions) ? 'translateY(5px)' : 'translateY(-15px)',
    } : {
      transform: [{ translateY: ((isLastMessage) || showMessageActions) ? 5 : -15 }],
    }),
    minWidth: isUser ? 150 : 250,
    width: '100%',
  }), [isLastMessage, showMessageActions, isUser]);

  return (
    <View style={containerStyle}>
      {message.role === 'assistant' ? botAvatar : null}
      <div
        style={wrapperStyle}
        className={`message-bubble-wrapper ${(message.isError || message?.flags?.isDeleted) ? 'error' : ''}`}
        data-is-user={isUser ? 'true' : 'false'}
        data-message-id={message.id}
        data-is-last-message={isLastMessage ? 'true' : 'false'}
        data-is-animated={showMessageActions ? 'false' : 'true'}
      >
        <MessageBubbleContent
          conversationId={conversationId}
          message={message}
          colors={colors}
          isUser={isUser}
          onOpenLightbox={onOpenLightbox}
          onOpenFileEditor={onOpenFileEditor}
        />
        {isUser && <MessageReactions conversationId={conversationId} message={message} />}
        {shouldShowMessageTimestamp(message, { isUser, showTimestamps }) && (
          <Text size="xs" style={{ opacity: 0.5, marginTop: 3, textAlign: isUser ? 'right' : 'left', paddingHorizontal: 4 }}>
            {formatMessageTime(message.timestamp)}
          </Text>
        )}

        {showActions && (
          <div
            style={actionsStyle}
            className="message-bubble-actions-container"
          >
            <MessageActions
              conversationId={conversationId}
              message={message}
              isUser={isUser}
              userName={userName}
              userAvatar={userAvatar}
              isVisible={true}
              isCompact={true}
              onCopy={showCopy ? onCopy : undefined}
              onAddToEditor={showAddToEditor ? onAddToEditor : undefined}
              onAddToDocument={showAddToDocument ? onAddToDocument : undefined}
              onAddToSpreadsheet={showAddToSpreadsheet ? onAddToSpreadsheet : undefined}
              onShare={onShare}
              onSpeak={showCopy ? onSpeak : undefined}
              onRegenerate={isInMessageGroup ? null : onRegenerate}
              onFork={onFork}
              onStartNewTask={onStartNewTask}
              onBookmark={onBookmark}
              onFeedback={onFeedback}
              onEdit={onEdit}
              onReport={onReport}
              onExport={onExport}
              onDelete={onDelete}
              showAttribution={true}
              workActions={message.workActions}
              isLastMessage={isLastMessage}
              modelIds={displayModelIds || message.modelIds}
              agentIds={message.agentIds}
              isAutoMode={isAutoMode}
              inferences={message.inferences}
              isImage={Array.isArray(message.images) && message.images.length > 0}
              isFile={Array.isArray(message.files) && message.files.length > 0}
              isBookmarked={message?.flags?.isSaved === true}
              parameters={message.parameters}
              createdAt={message.timestamp}
              showPreferenceIcons={showPreferenceIcons}
              isGroupedResponsePreferred={message?.flags?.isGroupedResponsePreferred}
              onPreferResponse={onPreferResponse}
              onRejectResponse={onRejectResponse}
            />
          </div>
        )}
        {showPreferenceButton && onPreferResponse && (
          <View style={staticStyles.preferenceButtonContainer}>
            <Button
              text="I prefer this response"
              variant={isDarkTheme(theme) ? 'secondary' : 'tertiary'}
              size="small"
              onPress={onPreferResponse}
            />
          </View>
        )}
        {/* Don't show MessageButtons for credit/Plus/moderation errors since their message components handle the CTA */}
        {Array.isArray(message.buttons) && message.buttons.length > 0 && 
         !((message.isError || message.hasError) && (isCreditError(message) || isPlusError(message) || isModerationError(message))) && (
          <View>
            <MessageButtons 
              conversationId={conversationId}
              messageId={message.id}
              buttons={message.buttons} 
              onButtonClick={onButtonClick}
            />
          </View>
        )}
      </div>
    </View>
  );
});

const MessageBubble = ({ 
  message,
  conversationId,
  isLastMessage,
  onCopy,
  onAddToEditor,
  onAddToDocument,
  onAddToSpreadsheet,
  onShare,
  onSpeak,
  onRegenerate,
  onFork,
  onStartNewTask,
  onBookmark,
  onFeedback,
  onEdit,
  onReport,
  onDelete,
  onExport,
  onOpenLightbox,
  onOpenFileEditor,
  onButtonClick,
  messageWidth,
  showMessageActions,
  isInMessageGroup,
  isLastGroupInChat,
  isAutoMode = false,
  displayModelIds,
  botAvatar,
  onPreferResponse,
  onRejectResponse,
}) => {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const isMobile = Platform.OS !== 'web';
  const token = useAuthStore(state => state.token);
  const isTokenExpired = useAuthStore(state => state.isTokenExpired);
  const userName = useAuthStore(state => state.user?.fullName) || 'You';
  const userAvatar = useAuthStore(state => state.user?.avatarPath) || null;
  const showTimestamps = useUIStore(state => state.showTimestamps);

  const isUser = message.role === 'user';
  const isAuthenticated = token && !isTokenExpired();
  const showActions = isAuthenticated && !message.isError && !message.isTyping && !message.isStreaming && !message.isThinking && !message?.flags?.isDeleted;
  const showCopy = message.content && message.content.length > 0;
  const showAddToEditor = showCopy || messageHasEditorInsertContent(message);
  const showAddToDocument = !isUser && showAddToEditor;
  const showAddToSpreadsheet = !isUser && messageHasMarkdownTable(message);
  // Check if a preference has been locally selected for this group
  const hasSelectedPreference = message.flags?.hasSelectedPreferenceLocally;
  // Show button only on last group AND if no preference has been selected locally yet
  const showPreferenceButton = isInMessageGroup && isLastGroupInChat && !isUser && message.content && !hasSelectedPreference;
  // Show icons if not last group, OR if last group but preference has been selected
  const showPreferenceIcons = isInMessageGroup && !isUser && message.content && (!isLastGroupInChat || hasSelectedPreference);

  if (isMobile) {
    return (
      <MobileMessage
        conversationId={conversationId}
        message={message}
        messageWidth={messageWidth}
        colors={colors}
        theme={theme}
        isUser={isUser}
        isLastMessage={isLastMessage}
        showActions={showActions}
        showCopy={showCopy}
        showAddToEditor={showAddToEditor}
        showAddToDocument={showAddToDocument}
        showAddToSpreadsheet={showAddToSpreadsheet}
        showTimestamps={showTimestamps}
        onCopy={onCopy}
        onAddToEditor={onAddToEditor}
        onAddToDocument={onAddToDocument}
        onAddToSpreadsheet={onAddToSpreadsheet}
        onShare={onShare}
        onSpeak={onSpeak}
        onRegenerate={onRegenerate}
        onFork={onFork}
              onStartNewTask={onStartNewTask}
        onBookmark={onBookmark}
        onFeedback={onFeedback}
        onEdit={onEdit}
        onReport={onReport}
        onDelete={onDelete}
        onExport={onExport}
        onOpenLightbox={onOpenLightbox}
        onOpenFileEditor={onOpenFileEditor}
        onButtonClick={onButtonClick}
        userName={userName}
        userAvatar={userAvatar}
        isInMessageGroup={isInMessageGroup}
        showPreferenceButton={showPreferenceButton}
        showPreferenceIcons={showPreferenceIcons}
        onPreferResponse={onPreferResponse}
        onRejectResponse={onRejectResponse}
        isLastGroupInChat={isLastGroupInChat}
        isAutoMode={isAutoMode}
        displayModelIds={displayModelIds}
        botAvatar={botAvatar}
      />
    );
  }

  return (
    <DesktopMessage
      conversationId={conversationId}
      message={message}
      colors={colors}
      theme={theme}
      isUser={isUser}
      isStreaming={message.isStreaming}
      showActions={showActions}
      showCopy={showCopy}
      showAddToEditor={showAddToEditor}
      showAddToDocument={showAddToDocument}
      showAddToSpreadsheet={showAddToSpreadsheet}
      showTimestamps={showTimestamps}
      isLastMessage={isLastMessage}
      messageWidth={messageWidth}
      showMessageActions={showMessageActions}
      onCopy={onCopy}
      onAddToEditor={onAddToEditor}
      onAddToDocument={onAddToDocument}
      onAddToSpreadsheet={onAddToSpreadsheet}
      onShare={onShare}
      onSpeak={onSpeak}
      onRegenerate={onRegenerate}
      onFork={onFork}
              onStartNewTask={onStartNewTask}
      onBookmark={onBookmark}
      onFeedback={onFeedback}
      onEdit={onEdit}
      onExport={onExport}
      onButtonClick={onButtonClick}
      onReport={onReport}
      onDelete={onDelete}
      isXS={isXS}
      onOpenLightbox={onOpenLightbox}
      onOpenFileEditor={onOpenFileEditor}
      userName={userName}
      userAvatar={userAvatar}
      isInMessageGroup={isInMessageGroup}
      showPreferenceButton={showPreferenceButton}
      showPreferenceIcons={showPreferenceIcons}
      onPreferResponse={onPreferResponse}
      onRejectResponse={onRejectResponse}
      isLastGroupInChat={isLastGroupInChat}
      isAutoMode={isAutoMode}
      displayModelIds={displayModelIds}
      botAvatar={botAvatar}
    />
  );
};

MessageBubble.propTypes = {
  conversationId: PropTypes.string.isRequired,
  message: PropTypes.shape({
    role: PropTypes.string.isRequired,
    content: PropTypes.string,
    isError: PropTypes.bool,
    hasError: PropTypes.bool,
    isTyping: PropTypes.bool,
    isStreaming: PropTypes.bool,
    isThinking: PropTypes.bool,
    isWaiting: PropTypes.bool,
    isRequestingMedia: PropTypes.string, // image, video, audio, pdf, etc
    thinkingContent: PropTypes.string,
    thinkingDurationMs: PropTypes.number,
    images: PropTypes.array,
    buttons: PropTypes.array,
    inferences: PropTypes.array,
    modelIds: PropTypes.array,
    agentIds: PropTypes.array,
  }).isRequired,
  isLastMessage: PropTypes.bool,
  onCopy: PropTypes.func,
  onAddToEditor: PropTypes.func,
  onAddToDocument: PropTypes.func,
  onAddToSpreadsheet: PropTypes.func,
  onShare: PropTypes.func,
  onSpeak: PropTypes.func,
  onRegenerate: PropTypes.func,
  onFork: PropTypes.func,
  onStartNewTask: PropTypes.func,
  onBookmark: PropTypes.func,
  onFeedback: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onExport: PropTypes.func,
  onReport: PropTypes.func,
  onButtonClick: PropTypes.func,
  isInMessageGroup: PropTypes.bool,
  isLastGroupInChat: PropTypes.bool,
  isAutoMode: PropTypes.bool,
  displayModelIds: PropTypes.arrayOf(PropTypes.string),
  onPreferResponse: PropTypes.func,
  onRejectResponse: PropTypes.func,
};

export default React.memo(MessageBubble);
